import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import authService from "../services/authService";
import { CPD_PROGRAMS } from "./CPDprograms/types";
// import ChangeEmailPage from "./ChangeEmailPage/ChangeEmailPage";
import ChangePasswordPage from "./ChangePasswordPage/ChangePasswordPage";
import ContactSupportPage from "./ForgotPasswordPage/ContactSupportPage";
import ForgotSharedAccountPage from "./ForgotPasswordPage/ForgotSharedAccountPage";
import MFAAuthenticationPage from "./MFAAuthentication/MFAAuthenticationPage";
import MFAEnrolmentPage from "./MFAEnrolment/MFAEnrolmentPage";
import StudyPage from "./study/StudyPage";
import PatientListPage from "./PatientListPage/PatientListPage";
import EpisodeListPage from "./PatientEpisodesPage/EpisodeListPage";
import CPDprogramsPage from "./CPDprograms/CPDprogramsPage";
import CPDcytologyPage from "./CPDprograms/CPDcytologyPage";
import CPDdiabetePages from "./CPDprograms/CPDdiabetesPage";
import CPDskinPage from "./CPDprograms/CPDskinPage";
import { isTenantVet } from "../services/tenantService";
import DeleteAccountPage from "./DeleteAccountPage/DeleteAccountPage";
import CognitoLoginPage from "./LoginPage/CognitoLoginPage";

type ProtectedRouteProps = {
  component: React.ComponentType;
  isAuth: boolean;
  title?: string;
  isCognito: boolean;
};

class redirectValidator {
  public component: React.ComponentType;

  constructor(chckingComponent: React.ComponentType) {
    this.component = chckingComponent;
  }

  public checkingList = [
    this.checkIsExpired,
    this.checkIsLogin,
    this.checkIsBypassVerify,
    this.checkIsVerified,
    this.checkForceEnrol,
    this.checkIfClinicalTrials,
    this.checkStudyPageAccess,
    this.checkEpisodeListPageAccess,
    this.checkCPDprogramAccess,
    this.checkDeleteAccountPageAccess,
    this.checkCognitoEnrolled,
    this.preventPasswordChange,
  ];
  public autoPlay() {
    //auto play all validations.
    for (let i = 0; i < this.checkingList.length; i++) {
      const resFun = this.checkingList[i].bind(this);
      const res = resFun();
      if (res) {
        return res;
      }
    }
    return "";
  }
  
  public preventPasswordChange() {
    if (this.component === ChangePasswordPage && authService.isCognito()) {
      return "/patients";
    }
  }

  public checkIsLogin() {
    //if user is not login, rediret to login page'
    if (!authService.isAuthenticated()) {
      return "/login";
    }
  }

  public checkIsBypassVerify() {
    //if user enalbe enrolment and bypass the verification
    if (
      authService.getMFAenrolled() == true &&
      authService.getOtpVerfiedEnrolled() == false &&
      authService.getForceAuth() == true &&
      this.component !== MFAAuthenticationPage
    ) {
      return "/mfaauthentication";
    }
  }

  public checkIsVerified() {
    //check is has otp verfied and try to acess mfaauth or enrol page, if verfied redirect to patient page.
    if (
      authService.getOtpVerfiedEnrolled() == true &&
      (this.component === MFAAuthenticationPage || this.component === MFAEnrolmentPage)
    ) {
      if (authService.isClinicalTrial() && !authService.isMobility()) {
        return "/patient-episodes";
      } else {
        return "/patients";
      }
    }
  }

  public checkForceEnrol() {
    //if user force enrol, redirect to enrol page.
    if (authService.getForceEnrol() && !authService.getDelegated() && this.component !== MFAEnrolmentPage) {
      return "/enrolment";
    }
  }

  public checkIsExpired() {
    //user password expired, redite to change password page
    if (
      authService.getExpired() &&
      authService.getOtpVerfiedEnrolled() == true &&
      this.component !== ChangePasswordPage &&
      this.component !== MFAAuthenticationPage
    ) {
      return "/change-password";
    }
  }

  public checkIfClinicalTrials() {
    // Clinical Trials users have their own Landing Page
    if (authService.isClinicalTrial() && !authService.isMobility() && this.component === PatientListPage) {
      return "/patient-episodes";
    }
  }

  public checkStudyPageAccess() {
    if ((!authService.isHospitalAdmin() || !authService.isClinicalTrial()) && this.component === StudyPage) {
      return "/patients";
    }
  }

  public checkEpisodeListPageAccess() {
    if (this.component === EpisodeListPage && (!authService.isClinicalTrial() || authService.isMobility())) {
      return "/patients";
    }
  }

  public checkCognitoEnrolled() {
    if (this.component !== CognitoLoginPage && authService.isCognito() && !authService.getCognitoEnrolled()) {
      return "/login-sso";
    }
  }

  public checkCPDprogramAccess() {
    const cpdEnrolments: Array<number> = authService.getCPDEnrolled();
    let defaultReturnPage = "/patients";

    ////if not enrol any CPD but has valid providers, still able to access enrol page.
    if (cpdEnrolments.length > 0 || authService.getCpdProviders()?.length) {
      defaultReturnPage = "./cpd-programs";
    }
    switch (this.component) {
      case CPDcytologyPage:
        if (isTenantVet() || !cpdEnrolments.includes(CPD_PROGRAMS.CYTOLOGY_EVALUATION_PROGRAM)) {
          return defaultReturnPage;
        }
        break;
      case CPDdiabetePages:
        if (isTenantVet() || !cpdEnrolments.includes(CPD_PROGRAMS.DIABETES_CLINICAL_EVALUATION_PROGRAM)) {
          return defaultReturnPage;
        }
        break;
      case CPDskinPage:
        if (isTenantVet() || !cpdEnrolments.includes(CPD_PROGRAMS.SKIN_PATHOLOGY_EVALUATION_PROGRAM)) {
          return defaultReturnPage;
        }
        break;
      case CPDprogramsPage:
        if (isTenantVet() || !authService.getCpdProviders()?.length) {
          return "/patients";
        }
    }
  }

  public checkDeleteAccountPageAccess() {
    if (this.component === DeleteAccountPage && authService.getDelegatedUser()) {
      return "/account-settings";
    }
  }
}

export const redirectPath = (component: React.ComponentType): string => {
  const validator = new redirectValidator(component);
  const redirectUrl = validator.autoPlay();
  if (redirectUrl) {
    return redirectUrl as string;
  }
  return "";
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ component: Component, isAuth, title, isCognito }) => {
  const location = useLocation();

  if (!isAuth) {
    return <Navigate to={isCognito ? "/login-sso" : "/login"} state={{ from: location }} replace />;
  }

  if (title) {
    document.title = title;
  }

  const path = redirectPath(Component);

  if (path) {
    return <Navigate to={path} state={{ from: location }} replace />;
  }

  return <Component />;
};

type PrivateRouteProps = {
  isAuth: boolean;
  title?: string;
  isCognito: boolean;
  children: React.ReactNode;
};

export const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, isAuth, title, isCognito }) => {
  const location = useLocation();

  if (!isAuth) {
    return <Navigate to={isCognito ? "/login-sso" : "/login"} state={{ from: location }} replace />;
  }

  if (title) {
    document.title = title;
  }

  return authService.isAuthenticated() ? (
    <>{children}</>
  ) : (
    <Navigate to={isCognito ? "/login-sso" : "/login"} state={{ from: location }} replace />
  );
};

export const SwitchingRoute: React.FC<{ component: React.ComponentType }> = ({ component: Component }) => {
  if (Component === ContactSupportPage) {
    return authService.getSharedAccount() ? <ForgotSharedAccountPage /> : <Component />;
  }
  return <Component />;
};

export default ProtectedRoute;
